import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import { Lightbulb, Target, BarChart3 } from "lucide-react";
import { Skeleton } from "@chotca/ui";
import { useOrgInsights, usePlanStatus } from "@chotca/api-client";
import type { InsightTip } from "@chotca/config";
import { useSubscriptionModals } from "@/components/employer/subscription-modals";
import { PostingQuotaCard } from "@/components/posting-quota-card";

// Right-rail companion to the postings list. Answers three implicit
// employer questions: "is this channel working?", "do I have sourcing left to
// keep going?", and "what should I do next?". Tips scoped to a specific
// posting are rendered inline on that card by the list — the rail only
// surfaces org-level tips (no `postingId`).
export function InsightRail({ orgId }: { orgId: string | null | undefined }) {
  const { data, isLoading, isError } = useOrgInsights(orgId);

  if (!orgId) return null;

  return (
    <aside className="space-y-4">
      <PostingQuotaCard quota={data?.postingQuota} isLoading={isLoading} />
      <WeeklyStatCard
        isLoading={isLoading}
        isError={isError}
        applicants={data?.week.applicants}
        interviews={data?.week.interviews}
      />
      <SourcingCard orgId={orgId} />
      <TipsCard isLoading={isLoading} tips={data?.tips ?? []} />
    </aside>
  );
}

function WeeklyStatCard({
  isLoading,
  isError,
  applicants,
  interviews,
}: {
  isLoading: boolean;
  isError: boolean;
  applicants?: number;
  interviews?: number;
}) {
  const t = useTranslations("postings.rail");
  return (
    <section className="rounded-2xl bg-ink p-5 text-paper">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-paper/60">
        <BarChart3 className="h-3.5 w-3.5" aria-hidden />
        <span>{t("weeklyTitle")}</span>
      </div>
      {isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-7 w-24 bg-paper/10" />
          <Skeleton className="h-4 w-32 bg-paper/10" />
        </div>
      ) : isError ? (
        <p className="mt-3 text-sm text-paper/70">{t("weeklyError")}</p>
      ) : (
        <>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold leading-none text-lime">
              {applicants ?? 0}
            </span>
            <span className="text-sm text-paper/70">
              {t("weeklyApplicants")}
            </span>
          </div>
          <div className="mt-1.5 flex items-baseline gap-2 text-sm text-paper/70">
            <span className="font-semibold text-paper">{interviews ?? 0}</span>
            <span>{t("weeklyInterviews")}</span>
          </div>
        </>
      )}
    </section>
  );
}

// Remaining "săn ứng viên" allowance for the current plan period (+ à-la-carte
// extra). Replaces the old interview-credit wallet. FREE orgs can't source, so
// the CTA nudges an upgrade; Pro/Business can buy a top-up pack.
function SourcingCard({ orgId }: { orgId: string | null | undefined }) {
  const t = useTranslations("postings.rail");
  const { data, isLoading } = usePlanStatus(orgId);
  const { openUpgrade, openTopup } = useSubscriptionModals();

  const isFree = (data?.effectiveCode ?? "FREE") === "FREE";
  const remaining = data?.sourcingRemaining ?? 0;

  return (
    <section className="rounded-2xl border border-smoke bg-paper p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-ink/60">
          <Target className="h-3.5 w-3.5" aria-hidden />
          <span>{t("sourcingTitle")}</span>
        </div>
        <button
          type="button"
          onClick={() => (isFree ? openUpgrade() : openTopup("SOURCING"))}
          className="text-[11px] font-semibold text-ink underline-offset-2 hover:underline"
        >
          {isFree ? t("sourcingUpgrade") : t("sourcingBuyMore")}
        </button>
      </div>
      {isLoading ? (
        <Skeleton className="mt-3 h-8 w-20" />
      ) : (
        <>
          <div className="mt-2 text-3xl font-extrabold leading-none text-ink">
            {remaining}
          </div>
          <p className="mt-1.5 text-xs text-ink/60">
            {isFree ? t("sourcingFree") : t("sourcingReady")}
          </p>
        </>
      )}
    </section>
  );
}

// Only org-scoped tips show up in the rail. Posting-scoped tips render
// inline on their card so the employer sees them in context, not as a
// disconnected list.
function TipsCard({
  isLoading,
  tips,
}: {
  isLoading: boolean;
  tips: InsightTip[];
}) {
  const t = useTranslations("postings.rail");
  const orgTips = tips.filter((tip) => !tip.postingId);

  return (
    <section className="rounded-2xl border border-smoke bg-paper p-5">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-ink/60">
        <Lightbulb className="h-3.5 w-3.5" aria-hidden />
        <span>{t("tipsTitle")}</span>
      </div>
      {isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : orgTips.length === 0 ? (
        <p className="mt-3 text-xs text-ink/60">{t("tipsEmpty")}</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {orgTips.map((tip) => (
            <TipRow key={tip.id} tip={tip} />
          ))}
        </ul>
      )}
    </section>
  );
}

function TipRow({ tip }: { tip: InsightTip }) {
  const { openUpgrade } = useSubscriptionModals();
  const href = tip.cta === "EDIT_ORG" ? "/org" : null;
  const body = <p className="text-xs leading-snug text-ink">{tip.message}</p>;

  return (
    <li className="flex gap-2">
      <span aria-hidden>•</span>
      <div className="flex-1">
        {href ? (
          <Link to={href} className="block hover:underline">
            {body}
          </Link>
        ) : tip.cta === "BUY_CREDITS" ? (
          // Legacy "buy credits" tip → route to the plan upgrade modal now.
          <button
            type="button"
            onClick={() => openUpgrade()}
            className="block w-full text-left hover:underline"
          >
            {body}
          </button>
        ) : (
          body
        )}
      </div>
    </li>
  );
}
